import { Building2, TriangleAlert } from 'lucide-react';
import type { ReactNode } from 'react';
import type { ApplicationStage, CrossApplication } from '@nest-recruit/shared';
import { STAGE_TONES } from '../../../components/status-tones';
import { ToneChip } from '../../../components/Tone';
import styles from './CandidateBits.module.css';
import { useStageLabels } from '../../../lib/modules';

export function StageChip({ stage }: { readonly stage: ApplicationStage }): ReactNode {
  const stageLabels = useStageLabels();
  return <ToneChip tone={STAGE_TONES[stage]}>{stageLabels[stage]}</ToneChip>;
}

/**
 * §L1: the stored score, and — when a must-have is missing — the flag that explains
 * why it cannot rise above 49. A sorting aid, never a decision.
 */
export function ScoreBadge({ score, isMissingMustHave }: { readonly score: number | null; readonly isMissingMustHave: boolean }): ReactNode {
  if (score === null) return <span className={styles.muted}>—</span>;
  return (
    <span className={styles.score}>
      <span className={styles.scoreValue}>{score}</span>
      {isMissingMustHave && (
        <span className={styles.capped} title="Missing a must-have">
          <TriangleAlert size={12} aria-hidden />
          Must-have missing
        </span>
      )}
    </span>
  );
}

/**
 * D1: "a cross-venture banner appears on every candidate screen when another live
 * application exists, showing the venture and stage but not the notes" — so two
 * ventures never compete for one person without knowing.
 */
export function CrossApplicationBanner({ applications, lead }: { readonly applications: readonly CrossApplication[]; readonly lead: string }): ReactNode {
  const stageLabels = useStageLabels();
  if (applications.length === 0) return null;
  return (
    <div className={styles.banner} role="note">
      <Building2 size={16} aria-hidden className={styles.bannerIcon} />
      <div className={styles.bannerBody}>
        <strong>{lead}</strong>
        <ul className={styles.bannerList}>
          {applications.map((application, index) => (
            <li key={`${application.ventureName}-${application.stage}-${index}`}>
              {application.ventureName} · {stageLabels[application.stage]}
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
}
